import { Amplify, ResourcesConfig } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser, signInWithRedirect, AuthSession } from 'aws-amplify/auth';
import { sessionStorage } from 'aws-amplify/utils';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAmplifyAuthConfig, getAppSyncConfig, getRestApiConfig } from 'src/components/context/AuthContextUtils';
import { configureTheLogger } from 'src/logger';
import { ADMIN_LDAP_GROUPS, eCacheKeys, eLDAPGroup } from '../../constants/AppConstants';
import { LoadingSpinner } from '../generic-components/LoadingSpinner';
import { LoadingStatus, UserAuthContext } from './AppContextModels';
import { RumClient } from 'src/services/aws/CloudWatchRumService';

const initialData: UserAuthContext = {
  userAuthDataLoadingStatus: LoadingStatus.Loading
} as UserAuthContext;
const AuthContextDetails = createContext(initialData);
export const useAuth = () => useContext(AuthContextDetails);

// Provider component that wraps your app and makes auth object
export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userAuthData = useAuthProvider();

  if (userAuthData?.userAuthDataLoadingStatus === LoadingStatus.Loading) {
    return <LoadingSpinner />;
  }

  configureTheLogger(userAuthData);
  RumClient.initialize(userAuthData.Alias);

  return <AuthContextDetails.Provider value={userAuthData}>{children}</AuthContextDetails.Provider>;
};

const useAuthProvider = () => {
  const [userCognitoAuthData, setUserCognitoAuthData] = useState<UserAuthContext>({
    ...initialData,
    auth_status_message: 'Loading',
    userAuthDataLoadingStatus: LoadingStatus.Loading
  });

  useEffect(() => {
    authenticateAndSetUserDetails();
  }, []);

  const authenticateAndSetUserDetails = () => {
    Amplify.configure(getAmplifyAuthConfig());

    const configureAppSync = async () => {
      try {
        const authConfig = getAmplifyAuthConfig();
        const awsAppSync = await getAppSyncConfig();
        const restApiConfig = getRestApiConfig();

        const combinedConfig = {
          ...authConfig,
          API: {
            ...awsAppSync.API,
            ...restApiConfig.API
          }
        };

        Amplify.configure(combinedConfig as ResourcesConfig);
      } catch (error: any) {
        // logger isn't configured at this point.
        console.error('Unable to fetch Config', error);
      }
    };

    const REDIRECT_KEY = 'auth_redirect_url';

    const signInWithAmazonFederate = async () => {
      try {
        // Preserve the original URL so we can restore it after auth redirect
        await sessionStorage.setItem(REDIRECT_KEY, window.location.href);
        await signInWithRedirect({
          provider: { custom: 'AmazonFederate' }
        });
        const session = await fetchAuthSession();
        const sessionDetails = await getSessionDetails(session);
        setUserCognitoAuthData(sessionDetails);
      } catch (error: any) {
        // logger isn't configured at this point.
        console.error('Unable to sign in with AmazonFederate', error);
        setUserCognitoAuthData({
          ...userCognitoAuthData,
          auth_status_message: 'Unable to sign in with AmazonFederate',
          userAuthDataLoadingStatus: LoadingStatus.Loading
        });
      }
    };

    getCurrentUser()
      .then(async () => {
        await configureAppSync();
        const session = await fetchAuthSession();
        const sessionDetails = await getSessionDetails(session);
        setUserCognitoAuthData(sessionDetails);

        // Restore the original URL after successful authentication
        const redirectUrl = await sessionStorage.getItem(REDIRECT_KEY);
        await sessionStorage.removeItem(REDIRECT_KEY);
        if (redirectUrl && redirectUrl !== window.location.href) {
          window.location.replace(redirectUrl);
          return;
        }
      })
      .catch(() => {
        signInWithAmazonFederate();
      });
  };

  /**
   * Retrieves and formats the LDAP groups from the credentials payload.
   * @param session The AWS auth session object containing the ID token payload.
   * @returns A properly formatted string array of LDAP groups.
   */
  const formatUserLDAPs = (session: AuthSession): string[] => {
    // if user is not part of any ldap, we receive it as "[]"
    const ldapGroups = session.tokens?.idToken?.payload?.['custom:LDAP_GROUPS'];
    // Check if ldapGroups is a string and try to parse it as JSON
    if (typeof ldapGroups === 'string') {
      try {
        const parsedLdapGroups = JSON.parse(ldapGroups);
        return Array.isArray(parsedLdapGroups) && parsedLdapGroups.every((item) => typeof item === 'string') ? (parsedLdapGroups as string[]) : [];
      } catch (error: any) {
        console.error('Failed to parse ldapGroups string: ', error);
        return [];
      }
    }

    // If ldapGroups is already an array, return it; otherwise, return an empty array
    return Array.isArray(ldapGroups) && ldapGroups.every((item) => typeof item === 'string') ? (ldapGroups as string[]) : [];
  };

  const getSessionDetails = async (session: AuthSession) => {
    const cachedDetails = await sessionStorage.getItem(eCacheKeys.UserAuthDetails);
    if (cachedDetails) {
      return JSON.parse(cachedDetails) as UserAuthContext;
    }

    const userLDAPs: string[] = formatUserLDAPs(session);
    const idTokenPayload = session.tokens?.idToken?.payload;

    const identities = idTokenPayload?.['identities'];
    const userId = Array.isArray(identities) && identities.length > 0 ? (identities[0] as any)?.userId : undefined;

    const sessionDetails = {
      Alias: userId,
      DisplayName: idTokenPayload?.['custom:DISPLAY_NAME'] as string,
      GivenName: idTokenPayload?.['custom:GIVEN_NAME'] as string,
      Email: idTokenPayload?.['custom:EMAIL'] as string,
      userLDAPGroups: userLDAPs,
      isDev: userLDAPs.includes(eLDAPGroup.DEV_LDAP),
      isAdmin: ADMIN_LDAP_GROUPS.some((group) => userLDAPs.includes(group)),
      isOEReadOnly: userLDAPs.includes(eLDAPGroup.OE_RO_LDAP),
      auth_status_message: 'SSO completed',
      userAuthDataLoadingStatus: LoadingStatus.Completed
    } as UserAuthContext;

    sessionStorage.setItem(eCacheKeys.UserAuthDetails, JSON.stringify(sessionDetails));
    return sessionDetails;
  };

  return userCognitoAuthData;
};
